import { useCallback, useEffect, useRef, useState } from 'react';
import Dialog from 'components/Overlays/AnimatedDialog';
import { FiAlertTriangle, FiFolder, FiLayers, FiX } from 'react-icons/fi';
import useAddCategory from 'api/CategoryResource/useAddCategory';
import useEditCategory from 'api/CategoryResource/useEditCategory';
import useDeleteCategory from 'api/CategoryResource/useDeleteCategory';
import useAddGenre from 'api/GenreResource/useAddGenre';
import useEditGenre from 'api/GenreResource/useEditGenre';
import useDeleteGenre from 'api/GenreResource/useDeleteGenre';
import usePortalDialog from 'layouts/Admin2/usePortalDialog';

export default function TaxonomyDialog({ selection, existing, onClose, visible, onExited }) {
    const { kind, intent, item } = selection;
    const [name, setName] = useState(item?.name || '');
    const [error, setError] = useState('');
    const dialogRef = useRef(null);
    const bindDialog = useCallback((node) => {
        dialogRef.current = node;
        (node?.querySelector('input:not([disabled])') || node?.querySelector('button'))?.focus();
    }, []);
    const triggerRef = useRef(selection.trigger || document.activeElement);
    const createCategory = useAddCategory(), editCategory = useEditCategory(item?.id), deleteCategory = useDeleteCategory(item?.id);
    const createCollection = useAddGenre(), editCollection = useEditGenre(item?.id), deleteCollection = useDeleteGenre(item?.id);
    const mutation = (kind === 'category'
        ? { create: createCategory, rename: editCategory, delete: deleteCategory }
        : { create: createCollection, rename: editCollection, delete: deleteCollection })[intent];
    const busy = mutation.isLoading;
    const busyRef = useRef(busy);
    busyRef.current = busy;
    const close = useCallback(() => { if (!busyRef.current) onClose(); }, [onClose]);
    usePortalDialog(visible, close, dialogRef, triggerRef);
    useEffect(() => {
        if (busy) dialogRef.current?.focus();
    }, [busy]);
    const deleting = intent === 'delete';
    const blocked = deleting && Number(item?.books_count) > 0;
    const action = deleting ? 'Delete' : intent === 'rename' ? 'Rename' : 'Create';
    const title = `${action} ${kind}`;
    const Icon = deleting ? FiAlertTriangle : kind === 'category' ? FiFolder : FiLayers;

    const submit = async (event) => {
        event.preventDefault();
        if (!visible || busy || blocked) return;
        const trimmed = name.trim();
        if (!deleting && !trimmed) { setError(`Enter a ${kind} name.`); return; }
        if (!deleting && existing.some((entry) => entry.id !== item?.id && entry.name.trim().toLowerCase() === trimmed.toLowerCase())) {
            setError(`A ${kind} with this name already exists.`); return;
        }
        setError('');
        try {
            await mutation.mutateAsync(deleting ? undefined : { name: trimmed });
            onClose();
        } catch (failure) { setError(failure?.response?.data?.message || `Couldn’t ${action.toLowerCase()} this ${kind}. Please try again.`); }
    };

    return <Dialog visible={visible} onHide={close} onExited={onExited} modal blockScroll dismissableMask={!busy} closeOnEscape={!busy} closable={false} focusOnShow={false} draggable={false} resizable={false} className="taxonomy-dialog"
        header={<div><div className={`taxonomy-dialog-icon${deleting ? ' taxonomy-danger' : ''}`}><Icon aria-hidden="true" /></div><h2 id="taxonomy-dialog-title">{title}</h2></div>}>
        <div className="taxonomy-dialog-body" ref={bindDialog} tabIndex={-1}
            onKeyDown={(event) => { if (busy && event.key === 'Tab') event.preventDefault(); }}>
            <button type="button" className="taxonomy-dialog-close" aria-label="Close dialog" disabled={busy} onClick={close}><FiX aria-hidden="true" /></button>
            <p id="taxonomy-dialog-description">{deleting
                ? blocked ? `“${item.name}” contains books. Move those books to another ${kind} before deleting it.` : `Delete “${item.name}”? This action cannot be undone. Only ${kind === 'category' ? 'categories' : 'collections'} without books can be deleted.`
                : kind === 'category' ? 'Use a broad name that helps readers navigate your library.' : 'Bring books together by genre, subject or a shared interest.'}</p>
            <form onSubmit={submit} aria-busy={busy}>
                {!deleting && <div className="taxonomy-dialog-field"><label htmlFor="taxonomy-name">{kind === 'category' ? 'Category' : 'Collection'} name</label>
                    <input id="taxonomy-name" value={name} onChange={(event) => { setName(event.target.value); setError(''); }} disabled={busy} aria-invalid={Boolean(error)} aria-describedby={error ? 'taxonomy-form-error' : 'taxonomy-name-hint'} placeholder={kind === 'category' ? 'e.g. Academic' : 'e.g. Science fiction'} autoComplete="off" />
                    <span id="taxonomy-name-hint">Choose a short, recognisable name.</span></div>}
                {error && <p className="taxonomy-form-error" id="taxonomy-form-error" role="alert">{error}</p>}
                <footer><button type="button" className="portal-button" onClick={close} disabled={busy}>{blocked ? 'Got it' : 'Cancel'}</button>
                    {!blocked && <button type="submit" className={`portal-button ${deleting ? 'taxonomy-delete-button' : 'portal-button-primary'}`} disabled={busy}>{busy ? deleting ? 'Deleting…' : 'Saving…' : `${action} ${kind}`}</button>}</footer>
            </form>
        </div>
    </Dialog>;
}
